import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  Eye,
  EyeOff,
  ExternalLink,
  Loader2,
  RefreshCcw,
  Shield,
  TrendingUp,
  XCircle,
} from 'lucide-react';

import type {
  ExecutionTraceState,
  ExecutionTraceStep,
  ExecutionTraceToolResult,
} from '@shared/orchestration';
import type { TraceStreamStatus } from '@/lib/execution-trace-stream-client';
import { useExecutionTraceStream } from '@/hooks/useExecutionTraceStream';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';

export type ExecutionTraceMode = 'compact' | 'expanded';

export interface ExecutionTraceProps {
  sessionId: number;
  messageId: number;
  requestId?: string;
  mode?: ExecutionTraceMode;
  onModeChange?: (mode: ExecutionTraceMode) => void;
  className?: string;
}

type StepResultsMap = Map<string, ExecutionTraceToolResult[]>;

const STATUS_ICON_MAP = {
  pending: Circle,
  running: Loader2,
  success: CheckCircle2,
  partial: AlertTriangle,
  failure: XCircle,
  timeout: AlertTriangle,
  skipped: Circle,
} as const;

const STATUS_COLOR_MAP: Record<string, string> = {
  pending: 'text-muted-foreground',
  running: 'text-sky-500',
  success: 'text-emerald-500',
  partial: 'text-amber-500',
  failure: 'text-red-500',
  timeout: 'text-red-500',
  skipped: 'text-muted-foreground',
};

const TOOL_BADGE_COLORS: Record<string, string> = {
  CRM_Tool: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-200',
  Pinecone_RAG: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-200',
  Web_Search: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-100',
  Web_Content: 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200',
  General_LLM: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200',
};

const DEFAULT_TOOL_BADGE =
  'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-200';

export const formatTraceDuration = (value?: number) => {
  if (!value || value < 0) return '—';
  if (value < 1000) return `${value}ms`;
  const seconds = value / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.round(seconds % 60);
  return `${minutes}m ${remainder}s`;
};

export const formatTraceCurrency = (value?: number, currency = 'USD') => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '$0.0000';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 4,
      maximumFractionDigits: 4,
    }).format(value);
  } catch {
    return `$${value.toFixed(4)}`;
  }
};

export const sanitizeTraceContent = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[unserializable payload]';
  }
};

export const shouldVirtualizeTraceList = (
  mode: ExecutionTraceMode,
  count: number,
) =>
  mode === 'expanded' && count > 25;

export const ExecutionTrace: React.FC<ExecutionTraceProps> = ({
  sessionId,
  messageId,
  requestId,
  mode = 'compact',
  onModeChange,
  className,
}) => {
  const [viewMode, setViewMode] = useState<ExecutionTraceMode>(mode);
  const hasRequestId = Boolean(requestId);
  const {
    trace,
    status,
    error,
    reconnect,
  } = useExecutionTraceStream({
    sessionId,
    messageId,
    enabled: hasRequestId,
    params: hasRequestId && requestId ? { requestId } : undefined,
  });

  useEffect(() => {
    setViewMode(mode);
  }, [mode]);

  const handleModeToggle = useCallback(
    (nextMode: ExecutionTraceMode) => {
      setViewMode(nextMode);
      onModeChange?.(nextMode);
    },
    [onModeChange],
  );

  const steps = useMemo(
    () =>
      trace.stepOrder
        .map((id) => trace.steps[id])
        .filter((step): step is ExecutionTraceStep => Boolean(step)),
    [trace.stepOrder, trace.steps],
  );

  const resultsByStep: StepResultsMap = useMemo(() => {
    const map: StepResultsMap = new Map();
    for (const result of trace.toolResults) {
      const stepId = result.metadata?.stepId;
      if (!stepId) continue;
      const existing = map.get(stepId) ?? [];
      existing.push(result);
      map.set(stepId, existing);
    }
    return map;
  }, [trace.toolResults]);

  const synthesisText = trace.synthesisChunks
    .map((chunk) => chunk.content)
    .join(' ');

  const canShowCosts = trace.cost?.visible !== false;

  const virtualizationEnabled = shouldVirtualizeTraceList(
    viewMode,
    trace.toolResults.length,
  );

  return (
    <Card className={cn('border border-muted shadow-sm', className)}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Activity className="h-4 w-4" aria-hidden />
            Execution transparency
            <Badge variant="secondary" className="ml-1 text-xs">
              {trace.plan?.steps?.length ?? 0} steps
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">
            {trace.isStreaming
              ? 'Streaming live orchestration events'
              : trace.completed
                ? 'Execution completed'
                : status === 'connecting'
                  ? 'Connecting to orchestrator…'
                  : 'Awaiting updates'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ModeToggle mode={viewMode} onChange={handleModeToggle} />
          <StatusPill status={status} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => reconnect(true)}
            className="gap-2 text-xs"
            aria-label="Retry execution trace stream"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>{error.type === 'parse' ? 'Parse error' : 'Stream error'}</AlertTitle>
            <AlertDescription className="flex items-center justify-between gap-3">
              <span>{error.message}</span>
              <Button size="sm" onClick={() => reconnect(true)}>
                Retry stream
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {trace.plan && (
          <PlanOverview plan={trace.plan} degradation={trace.degradation} />
        )}

        <div className="grid gap-4 lg:grid-cols-12">
          <Card className="lg:col-span-8">
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold text-foreground">
                Step progress
              </h3>
              <p className="text-xs text-muted-foreground">
                Monitor each orchestration step with live status indicators.
              </p>
            </div>
            <ScrollArea className="max-h-[420px]">
              <div className="divide-y">
                {steps.length === 0 && (
                  <div className="p-4 text-sm text-muted-foreground">
                    Awaiting plan data…
                  </div>
                )}
                {steps.map((step, index) => (
                  <StepCard
                    key={step.id}
                    step={step}
                    index={index}
                    results={resultsByStep.get(step.id) ?? []}
                  />
                ))}
              </div>
            </ScrollArea>
          </Card>
          <div className="space-y-4 lg:col-span-4">
            <Card>
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-foreground">
                  Execution cost
                </h3>
                <p className="text-xs text-muted-foreground">
                  Live cost tracking with budget guardrails.
                </p>
              </div>
              <div className="space-y-4 p-4">
                {canShowCosts ? (
                  <CostMeter cost={trace.cost} isLive={trace.isStreaming} />
                ) : (
                  <div className="rounded-md border border-dashed border-muted-foreground/40 p-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2 font-medium">
                      <Shield className="h-4 w-4" /> Cost hidden for privacy
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground/80">
                      Enable the Agentic Cost Transparency flag to display token and cost telemetry.
                    </p>
                  </div>
                )}
                {trace.degradation?.history?.length ? (
                  <DegradationTimeline history={trace.degradation.history} />
                ) : null}
                {trace.synthesisChunks.length > 0 && (
                  <SynthesisPreview content={synthesisText} isLive={trace.isStreaming} />
                )}
              </div>
            </Card>
          </div>
        </div>

        {viewMode === 'expanded' && (
          <DetailedResults
            results={trace.toolResults}
            virtualizationEnabled={virtualizationEnabled}
          />
        )}

        {trace.errors.length > 0 && (
          <ErrorSummary errors={trace.errors} />
        )}
      </div>
    </Card>
  );
};

const ModeToggle: React.FC<{
  mode: ExecutionTraceMode;
  onChange: (mode: ExecutionTraceMode) => void;
}> = ({ mode, onChange }) => (
  <div className="inline-flex rounded-md border bg-muted p-0.5 text-xs">
    <button
      type="button"
      className={cn(
        'rounded px-2 py-1 font-medium transition-colors',
        mode === 'compact'
          ? 'bg-background text-foreground shadow'
          : 'text-muted-foreground',
      )}
      onClick={() => onChange('compact')}
      aria-pressed={mode === 'compact'}
    >
      Compact
    </button>
    <button
      type="button"
      className={cn(
        'rounded px-2 py-1 font-medium transition-colors',
        mode === 'expanded'
          ? 'bg-background text-foreground shadow'
          : 'text-muted-foreground',
      )}
      onClick={() => onChange('expanded')}
      aria-pressed={mode === 'expanded'}
    >
      Expanded
    </button>
  </div>
);

const STATUS_PILL_COLORS: Record<TraceStreamStatus, string> = {
  idle: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-200',
  connecting: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-200',
  open: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200',
  error: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-200',
  closed: 'bg-muted text-muted-foreground',
};

const STATUS_PILL_LABELS: Record<TraceStreamStatus, string> = {
  idle: 'Idle',
  connecting: 'Connecting',
  open: 'Live',
  error: 'Error',
  closed: 'Closed',
};

const StatusPill: React.FC<{ status: TraceStreamStatus }> = ({ status }) => {
  const color = STATUS_PILL_COLORS[status] ?? 'bg-muted text-muted-foreground';
  const label = STATUS_PILL_LABELS[status] ?? status;

  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium', color)}>
      <span
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          status === 'open'
            ? 'bg-emerald-500'
            : status === 'error'
              ? 'bg-red-500'
              : 'bg-muted-foreground',
        )}
      />
      {label}
    </span>
  );
};

const PlanOverview: React.FC<{
  plan: ExecutionTraceState['plan'];
  degradation?: ExecutionTraceState['degradation'];
}> = ({ plan, degradation }) => {
  if (!plan) return null;

  return (
    <Card>
      <div className="border-b px-4 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <span>Plan overview</span>
          <Badge variant="outline">{plan.steps.length} steps</Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Strategy: {plan.synthesisStrategy}
          {plan.degraded && (
            <span className="ml-2 inline-flex items-center gap-1 text-amber-500">
              <AlertTriangle className="h-3 w-3" />
              Degraded
            </span>
          )}
        </p>
      </div>
      <div className="space-y-3 p-4 text-sm text-muted-foreground">
        <p>{plan.reasoning}</p>
        {degradation?.history?.length ? (
          <div className="rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
            <div className="flex items-center gap-2 font-semibold">
              <AlertTriangle className="h-4 w-4" />
              Degradation events
            </div>
            <ul className="mt-2 list-disc space-y-1 pl-4">
              {degradation.history.slice(-2).map((event) => (
                <li key={event.timestamp}>
                  {event.level} • {event.reason} @{' '}
                  {new Date(event.timestamp).toLocaleTimeString()}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
};

const StepCard: React.FC<{
  step: ExecutionTraceStep;
  index: number;
  results: ExecutionTraceToolResult[];
}> = ({ step, index, results }) => {
  const [expanded, setExpanded] = useState(false);
  const Icon = STATUS_ICON_MAP[step.status] ?? Circle;
  const iconClass = cn(
    'h-4 w-4',
    STATUS_COLOR_MAP[step.status] ?? 'text-muted-foreground',
    step.status === 'running' && 'animate-spin',
  );

  return (
    <div className="p-4">
      <button
        type="button"
        className="flex w-full items-start gap-3 text-left focus-visible:outline-none focus-visible:ring"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <div className="flex h-6 w-6 items-center justify-center rounded-full border border-muted-foreground/30 text-xs font-semibold text-muted-foreground">
          {index + 1}
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            {step.label || `Step ${index + 1}`}
            <Icon className={iconClass} aria-hidden />
            <span className="sr-only">Status: {step.status}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {step.tools.map((tool) => (
              <span
                key={tool.tool}
                className={cn(
                  'rounded-full px-2 py-0.5 font-medium',
                  TOOL_BADGE_COLORS[tool.tool] ?? DEFAULT_TOOL_BADGE,
                )}
              >
                {tool.tool}
              </span>
            ))}
            <span>Duration: {formatTraceDuration(step.durationMs)}</span>
          </div>
        </div>
        {expanded ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <div className="mt-3 space-y-3 rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {results.length === 0 && (
            <p>No tool outputs yet.</p>
          )}
          {results.map((result) => (
            <ToolResult key={result.id} result={result} />
          ))}
        </div>
      )}
    </div>
  );
};

const ToolResult: React.FC<{ result: ExecutionTraceToolResult }> = ({
  result,
}) => {
  const isSensitive = result.metadata.sensitive;
  return (
    <div className="space-y-2 rounded-md bg-muted/40 p-3">
      <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-foreground">
        {result.toolName}
        <Badge variant="outline" className="text-[10px] uppercase">
          {result.status}
        </Badge>
        {result.metadata.retryCount > 0 && (
          <Badge variant="secondary" className="text-[10px] uppercase">
            Retry {result.metadata.retryCount}
          </Badge>
        )}
      </div>
      <div className="rounded-md bg-background p-3 text-xs text-muted-foreground">
        {isSensitive ? (
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-300">
            <Shield className="h-3.5 w-3.5" />
            Sensitive output redacted
          </div>
        ) : (
          <pre className="max-h-48 overflow-auto text-xs leading-relaxed whitespace-pre-wrap">
            {sanitizeTraceContent(result.payload)}
          </pre>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
        {typeof result.metadata.executionTime === 'number' && (
          <span>Latency: {formatTraceDuration(result.metadata.executionTime)}</span>
        )}
        {typeof result.metadata.tokensUsed === 'number' && (
          <span>Tokens: {result.metadata.tokensUsed}</span>
        )}
        {typeof result.metadata.costUsd === 'number' && (
          <span>Cost: {formatTraceCurrency(result.metadata.costUsd)}</span>
        )}
      </div>
      {result.metadata.sources?.length ? (
        <div className="space-y-1 text-xs text-muted-foreground">
          <p className="font-semibold text-foreground">Sources</p>
          {result.metadata.sources.map((source) => (
            <div key={`${source.label}-${source.url ?? source.context ?? ''}`} className="flex items-center gap-2">
              <span>{source.label}</span>
              {source.url && (
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-foreground hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Open
                </a>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const CostMeter: React.FC<{
  cost?: ExecutionTraceState['cost'];
  isLive: boolean;
}> = ({ cost, isLive }) => {
  const [displayCost, setDisplayCost] = useState(cost?.totalUsd ?? 0);

  useEffect(() => {
    if (typeof cost?.totalUsd !== 'number') return;
    const diff = cost.totalUsd - displayCost;
    if (Math.abs(diff) < 0.0001) {
      setDisplayCost(cost.totalUsd);
      return;
    }
    const steps = 20;
    const increment = diff / steps;
    const interval = setInterval(() => {
      setDisplayCost((prev) => {
        const next = prev + increment;
        if ((increment > 0 && next >= cost.totalUsd) || (increment < 0 && next <= cost.totalUsd)) {
          clearInterval(interval);
          return cost.totalUsd;
        }
        return next;
      });
    }, 60);
    return () => clearInterval(interval);
  }, [cost?.totalUsd, displayCost]);

  const budget = cost?.budgetUsd;
  const percentUsed =
    typeof cost?.percentUsed === 'number'
      ? cost.percentUsed
      : budget
        ? Math.min(100, ((cost?.totalUsd ?? 0) / budget) * 100)
        : undefined;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm font-semibold text-foreground">
        <span>Running cost</span>
        {isLive && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
            Live
          </span>
        )}
      </div>
      <div className="text-2xl font-bold tracking-tight">
        {formatTraceCurrency(displayCost)}
      </div>
      {typeof percentUsed === 'number' && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{percentUsed.toFixed(1)}% of budget</span>
            {budget && <span>Budget {formatTraceCurrency(budget)}</span>}
          </div>
          <Progress
            value={percentUsed}
            className={cn(
              percentUsed > 85 && 'bg-red-100',
            )}
          />
        </div>
      )}
      {cost?.perToolUsd && (
        <details className="text-xs">
          <summary className="cursor-pointer text-foreground">
            Breakdown
          </summary>
          <div className="mt-2 space-y-1">
            {Object.entries(cost.perToolUsd).map(([tool, value]) => (
              <div key={tool} className="flex items-center justify-between">
                <span className="font-medium text-muted-foreground">{tool}</span>
                <span>{formatTraceCurrency(value)}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

const SynthesisPreview: React.FC<{
  content: string;
  isLive: boolean;
}> = ({ content, isLive }) => (
  <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
    <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
      <TrendingUp className="h-4 w-4" />
      Live synthesis
      {isLive && <span className="text-[10px] uppercase text-emerald-500">streaming</span>}
    </div>
    <p className="mt-2 line-clamp-4">{content || 'Awaiting synthesis chunks…'}</p>
  </div>
);

const DegradationTimeline: React.FC<{
  history: NonNullable<ExecutionTraceState['degradation']>['history'];
}> = ({ history }) => {
  if (!history) return null;
  return (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-900 dark:text-amber-200">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-4 w-4" />
        Degradation events
      </div>
      <ul className="mt-2 space-y-1">
        {history.slice(-3).map((event) => (
          <li key={event.timestamp}>
            <span className="font-semibold">{event.level}</span> – {event.reason}{' '}
            at {new Date(event.timestamp).toLocaleTimeString()}
          </li>
        ))}
      </ul>
    </div>
  );
};

const DetailedResults: React.FC<{
  results: ExecutionTraceToolResult[];
  virtualizationEnabled: boolean;
}> = ({ results, virtualizationEnabled }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [range, setRange] = useState({ start: 0, end: 15 });
  const rowHeight = 140;

  useEffect(() => {
    if (!virtualizationEnabled) return;
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (!container) return;
      const scrollTop = container.scrollTop;
      const visible = container.clientHeight;
      const start = Math.floor(scrollTop / rowHeight);
      const visibleCount = Math.ceil(visible / rowHeight) + 5;
      setRange({
        start: Math.max(0, start - 2),
        end: Math.min(results.length, start + visibleCount),
      });
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [results.length, virtualizationEnabled]);

  const visibleResults = virtualizationEnabled
    ? results.slice(range.start, range.end)
    : results;

  return (
    <Card>
      <div className="border-b px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          Tool outputs ({results.length})
        </h3>
        <p className="text-xs text-muted-foreground">
          Chronological tool results with streaming updates.
        </p>
      </div>
      <div
        ref={containerRef}
        className={cn(
          'relative',
          virtualizationEnabled ? 'max-h-[420px] overflow-y-auto' : 'overflow-visible',
        )}
      >
        {virtualizationEnabled ? (
          <div style={{ height: results.length * rowHeight, position: 'relative' }}>
            {visibleResults.map((result, idx) => {
              const offset = (range.start + idx) * rowHeight;
              return (
                <div
                  key={result.id}
                  style={{
                    position: 'absolute',
                    top: offset,
                    left: 0,
                    right: 0,
                    height: rowHeight - 12,
                  }}
                  className="border-b px-4 py-3"
                >
                  <ToolResult result={result} />
                </div>
              );
            })}
          </div>
        ) : (
          <div className="divide-y">
            {results.map((result) => (
              <div key={result.id} className="px-4 py-3">
                <ToolResult result={result} />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};

const ErrorSummary: React.FC<{
  errors: ExecutionTraceState['errors'];
}> = ({ errors }) => (
  <Card>
    <div className="border-b px-4 py-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <AlertCircle className="h-4 w-4 text-red-500" />
        Errors
      </div>
      <p className="text-xs text-muted-foreground">
        Standardized error codes with context and recovery hints.
      </p>
    </div>
    <div className="divide-y">
      {errors.map((err, idx) => (
        <div key={`${err.code}-${idx}`} className="px-4 py-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 font-semibold text-foreground">
            {err.code}
            <span className="text-xs text-muted-foreground">
              Phase: {err.context?.phase}
            </span>
          </div>
          <p className="mt-1 text-xs">{err.message}</p>
          {err.context?.correlationId && (
            <p className="mt-1 text-[11px] text-muted-foreground/80">
              Correlation ID: {err.context.correlationId}
            </p>
          )}
          {err.context?.requestId && (
            <p className="text-[11px] text-muted-foreground/80">
              Request ID: {err.context.requestId}
            </p>
          )}
        </div>
      ))}
    </div>
  </Card>
);

export default ExecutionTrace;
